<script lang="ts">
    import {CheckCircle2, Star} from '@lucide/svelte';
    import type {FormData, UpdateField} from '../../form/types';


    let {
        valid = $bindable(false),
        formData,
        updateField,
    }: {
        valid?: boolean;
        formData: FormData;
        updateField: UpdateField;
    } = $props();

    const readChecked = (event: Event): boolean =>
        (event.currentTarget as HTMLInputElement).checked;

    const FALLBACK = 'Niet ingevuld';
    const LEVEL_LABELS: Record<string, string> = {
        BASIS: 'Basis',
        GERICHT: 'Gericht',
        EXPERT: 'Expert',
    };

    const formatText = (value: string | null | undefined): string => {
        if (!value) {
            return FALLBACK;
        }

        const trimmed = (value + '').trim();
        return trimmed.length > 0 ? trimmed : FALLBACK;
    };

    const formatBoolean = (value: boolean | null): string => {
        if (value === null) {
            return 'Nog niet opgegeven';
        }

        return value ? 'Ja' : 'Nee';
    };

    const qualificationLabel = (qualification: string): string => {
        if (qualification !== 'Overig...') {
            return qualification;
        }

        return formatText(formData.otherQualificationText) === FALLBACK
            ? 'Overige opleiding'
            : formData.otherQualificationText.trim();
    };

    const methodologyLabel = (method: string): string => {
        if (method !== 'Anders...') {
            return method;
        }

        return formatText(formData.otherMethodologyText) === FALLBACK
            ? 'Andere methodiek'
            : formData.otherMethodologyText.trim();
    };

    const qualificationItems = $derived(
        formData.mainQualifications.map((qualification) => ({
            label: qualificationLabel(qualification),
            year: formatText(formData.qualificationYears[qualification]),
        })),
    );

    const top3Items = $derived(formData.top3Expertise.slice(0, 3));

    const otherExpertiseItems = $derived(
        formData.expertise
            .filter((theme) => !formData.top3Expertise.includes(theme))
            .map((theme) => ({
                theme,
                level: LEVEL_LABELS[formData.expertiseLevels[theme] ?? ''] ?? FALLBACK,
                education: formatText(formData.expertiseEducation[theme]),
                years: formatText(formData.expertiseYears[theme]),
            })),
    );

    const methodologyItems = $derived(
        formData.methodologies.map((method) => ({
            method: methodologyLabel(method),
            frequency: formatText(formData.methodologyFrequencies[method]),
            education: formatText(formData.methodologyEducation[method]),
            experience: formatText(formData.methodologyExpRange[method]),
        })),
    );

    $effect(() => {
        valid = formData.finalConsent
    })
</script>

<div class="space-y-6 animate-in fade-in duration-500"

>
    <h2 class="flex items-center gap-2 text-xl font-bold text-slate-800">
        <CheckCircle2 size={24} color="#7C46AF"/>
        Afronding
    </h2>

    <div class="space-y-4 rounded-[2.5rem] border border-slate-100 bg-slate-50 p-6">
        <h3 class="text-xs font-bold uppercase tracking-widest text-slate-400">Algemene informatie</h3>
        <div class="grid grid-cols-1 gap-4 text-xs md:grid-cols-2">
            <div class="flex flex-col">
                <span class="text-[10px] font-bold uppercase text-slate-400">Naam</span>
                <span class="font-semibold">{formatText(formData.name)}</span>
            </div>
            <div class="flex flex-col">
                <span class="text-[10px] font-bold uppercase text-slate-400">E-mail</span>
                <span class="font-semibold">{formatText(formData.email)}</span>
            </div>
            <div class="flex flex-col">
                <span class="text-[10px] font-bold uppercase text-slate-400">Werktelefoon</span>
                <span class="font-semibold">{formatText(formData.workPhone)}</span>
            </div>
            <div class="flex flex-col">
                <span class="text-[10px] font-bold uppercase text-slate-400">Geboortedatum</span>
                <span class="font-semibold">{formatText(formData.birthDate)}</span>
            </div>
            <div class="flex flex-col">
                <span class="text-[10px] font-bold uppercase text-slate-400">Rol</span>
                <span class="font-semibold">{formatText(formData.role)}</span>
            </div>
            <div class="flex flex-col">
                <span class="text-[10px] font-bold uppercase text-slate-400">Start 1-op-1 begeleiding</span>
                <span class="font-semibold">{formatText(formData.startYearOneOnOne)}</span>
            </div>
            <div class="flex flex-col md:col-span-2">
                <span class="text-[10px] font-bold uppercase text-slate-400">Praktijklocatie</span>
                <span class="font-semibold">{formatText(formData.address)}</span>
            </div>
            <div class="flex flex-col">
                <span class="text-[10px] font-bold uppercase text-slate-400">Inzetbaarheidsvorm</span>
                <span class="font-semibold">{formatText(formData.deliveryMode)}</span>
            </div>
            <div class="flex flex-col">
                <span class="text-[10px] font-bold uppercase text-slate-400">Uren per week</span>
                <span class="font-semibold">{formatText(formData.clientHoursRange)}</span>
            </div>
            <div class="flex flex-col md:col-span-2">
                <span class="text-[10px] font-bold uppercase text-slate-400">Beschikbare dagen</span>
                <span class="font-semibold"
                >{formData.availableDays.length > 0 ? formData.availableDays.join(', ') : FALLBACK}</span
                >
            </div>
        </div>
    </div>

    <div class="space-y-4 rounded-[2.5rem] border border-slate-100 bg-white p-6 shadow-sm">
        <h3 class="text-xs font-bold uppercase tracking-widest text-slate-400">Opleidingshistorie</h3>
        {#if qualificationItems.length === 0}
            <p class="text-xs font-semibold text-slate-500">Geen hoofdopleiding geselecteerd.</p>
        {:else}
            <div class="space-y-2">
                {#each qualificationItems as item}
                    <div class="flex items-center justify-between rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-xs">
                        <span class="font-semibold text-slate-700">{item.label}</span>
                        <span class="font-medium text-slate-500">Jaar: {item.year}</span>
                    </div>
                {/each}
            </div>
        {/if}
    </div>

    <div class="space-y-4 rounded-[2.5rem] border border-[#CDB6E2] bg-[#613689] p-6 text-white shadow-lg">
        <h3 class="text-xs font-bold uppercase tracking-widest text-[#E9DDF4]">Gedetailleerde expertises</h3>

        <div class="space-y-2">
            <p class="text-[11px] font-bold uppercase tracking-wide text-[#E9DDF4]">Top 3 specialismen</p>
            {#if top3Items.length === 0}
                <p class="text-xs font-semibold text-[#E9DDF4]">Geen top 3 geselecteerd.</p>
            {:else}
                <div class="flex flex-wrap gap-2">
                    {#each top3Items as topTheme}
                        <span class="inline-flex items-center gap-1 rounded-full border border-[#D8C4EA] bg-white/10 px-3 py-1 text-[11px] font-semibold">
                            <Star size={12} class="text-amber-300"/>
                            {topTheme}
                        </span>
                    {/each}
                </div>
            {/if}
        </div>

        <div class="space-y-2">
            <p class="text-[11px] font-bold uppercase tracking-wide text-[#E9DDF4]">Overige thema's</p>
            {#if otherExpertiseItems.length === 0}
                <p class="text-xs font-semibold text-[#E9DDF4]">Geen overige thema's geselecteerd.</p>
            {:else}
                <div class="space-y-2">
                    {#each otherExpertiseItems as item}
                        <div class="rounded-xl border border-[#A57CCB] bg-white/10 px-3 py-2 text-xs">
                            <div class="flex items-center justify-between gap-3">
                                <span class="font-semibold">{item.theme}</span>
                                <span class="rounded-full border border-[#D8C4EA] px-2 py-0.5 text-[10px] font-bold uppercase">{item.level}</span>
                            </div>
                            {#if item.level === 'Expert'}
                                <p class="mt-1 text-[11px] text-[#E9DDF4]">Opleiding: {item.education} -
                                    Ervaring: {item.years}</p>
                            {/if}
                        </div>
                    {/each}
                </div>
            {/if}
        </div>
    </div>

    <div class="space-y-4 rounded-[2.5rem] border border-amber-100 bg-amber-50 p-6">
        <h3 class="text-xs font-bold uppercase tracking-widest text-amber-700">Overbruggingszorg</h3>
        <div class="grid grid-cols-1 gap-3 text-xs md:grid-cols-2">
            <div class="rounded-xl border border-amber-200 bg-white px-3 py-2">
                <p class="text-[10px] font-bold uppercase text-amber-600">Beschikbaar voor overbruggingszorg</p>
                <p class="font-semibold text-amber-900">{formatBoolean(formData.openForBridgeCare)}</p>
            </div>
            <div class="rounded-xl border border-amber-200 bg-white px-3 py-2">
                <p class="text-[10px] font-bold uppercase text-amber-600">Bewust van BA/huisarts-afstemming</p>
                <p class="font-semibold text-amber-900">
                    {formData.openForBridgeCare === true ? formatBoolean(formData.baAwareness) : 'Niet van toepassing'}
                </p>
            </div>
        </div>
    </div>

    <div class="space-y-4 rounded-[2.5rem] border border-slate-100 bg-slate-50 p-6">
        <h3 class="text-xs font-bold uppercase tracking-widest text-slate-400">Capaciteit & methodieken</h3>
        {#if methodologyItems.length === 0}
            <p class="text-xs font-semibold text-slate-500">Geen methodieken geselecteerd.</p>
        {:else}
            <div class="space-y-2">
                {#each methodologyItems as item}
                    <div class="rounded-xl border border-slate-200 bg-white p-3 text-xs">
                        <p class="font-semibold text-slate-800">{item.method}</p>
                        <p class="mt-1 text-slate-600">Frequentie: <span
                                class="font-semibold text-slate-700">{item.frequency}</span></p>
                        <p class="text-slate-600">Opleiding: <span
                                class="font-semibold text-slate-700">{item.education}</span></p>
                        <p class="text-slate-600">Ervaringstermijn: <span
                                class="font-semibold text-slate-700">{item.experience}</span></p>
                    </div>
                {/each}
            </div>
        {/if}
    </div>

    <label
            class="group flex cursor-pointer items-start gap-4 rounded-[2.5rem] border border-slate-200 bg-white p-6 shadow-sm transition-all hover:border-blue-300"
    >
        <input
                type="checkbox"
                class="mt-1 h-5 w-5 rounded-lg text-blue-600 shadow-sm group-hover:scale-110"
                checked={formData.finalConsent}
                onchange={(event) => updateField('finalConsent', readChecked(event))}
        />
        <span class="text-[11px] italic leading-snug text-slate-600">
      Ik verklaar dat ik werk volgens de beroepscode van het <strong>NIP/NVO</strong> en dat deze
      informatie naar waarheid is ingevuld ten behoeve van optimale matching binnen SpecialistenNet.
    </span>
    </label>
</div>

<style>
    @import "tailwindcss";
</style>
